import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Badge, Button } from '@shared-ui/components';
import { adminApi } from '../../api/adminApi';
import { SessionTimeline } from './SessionTimeline';
import { sessionFindings } from './sessionFindings';
import { sevLabel, sevVariant } from './severityBadge';
import './PersonaAgents.css';

/** Run detail: findings + the per-session action transcript (timeline). */
export function PersonaAgentRunDetail() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['agent-run-detail', runId],
    queryFn: () => adminApi.getAgentRunDetail(runId!),
    enabled: !!runId,
  });

  if (isLoading) return <div className="settings-section">Loading…</div>;

  const run = data?.run;
  const sessions = data?.sessions ?? [];
  const feedback = data?.feedback ?? [];

  return (
    <div className="settings-section">
      <div className="section-header">
        <Button variant="ghost" size="sm" onClick={() => navigate('/persona-agents')}>← Back to runs</Button>
        <h1 className="pa-mono">{runId}</h1>
        {run && (
          <p className="section-description">
            status <strong>{run.status}</strong> · {(run.roster ?? []).join(', ')} · {run.tokensSpent?.toLocaleString() ?? '—'} tokens
          </p>
        )}
      </div>

      {/* Findings */}
      <h2 className="pa-h2">Findings ({feedback.length})</h2>
      {feedback.length === 0 && <p className="pa-muted">No feedback in this run.</p>}
      <div className="pa-feedback-list">
        {feedback.map(f => (
          <div key={f.id} className="pa-feedback">
            <div className="pa-feedback-head">
              {f.operation && <Badge variant="info" size="sm">{f.operation}</Badge>}
              <Badge variant="default" size="sm">{f.category}</Badge>
              <Badge variant={sevVariant(f.severity)} size="sm">sev {sevLabel(f.severity)}</Badge>
              <span className="pa-muted">{f.personaId}</span>
            </div>
            <div className="pa-feedback-obs">{f.observation}</div>
            {f.expectation && <div className="pa-feedback-exp">Expected: {f.expectation}</div>}
            <div className="pa-muted pa-mono">artifacts: {(f.artifacts ?? []).map(a => `${a.kind}:${a.ref}`).join(', ') || '—'}</div>
          </div>
        ))}
      </div>

      {/* Transcript — timeline of the session's steps */}
      <h2 className="pa-h2">Transcript</h2>
      {sessions.map(s => <SessionTimeline key={s.id} session={s} findings={sessionFindings(s, feedback)} />)}
    </div>
  );
}
